import React, { lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useSearchParams } from 'react-router-dom';
import { ThemeProvider, CssBaseline, CircularProgress, Box } from '@mui/material';
import { Toaster } from 'react-hot-toast';
import landingTheme from './theme';
import './signupLanding.css';

const LandingPage = lazy(() => import('./pages/LandingPage'));
const PricingPage = lazy(() => import('./pages/PricingPage'));
const AuthPage = lazy(() => import('./pages/AuthPage'));
const PaymentSuccessPage = lazy(() => import('./pages/PaymentSuccessPage'));
const ThankYouPage = lazy(() => import('./pages/ThankYouPage'));
const PrivacyPolicyPage = lazy(() => import('./pages/PrivacyPolicyPage'));
const TermsAndConditionsPage = lazy(() => import('./pages/TermsAndConditionsPage'));
const StripeConfigRequiredPage = lazy(() => import('./pages/StripeConfigRequiredPage'));

const PaymentFailureRedirect = () => {
  const [searchParams] = useSearchParams();
  const plan = searchParams.get('plan');
  const cycle = searchParams.get('cycle');
  const params = new URLSearchParams({ canceled: 'true' });
  if (plan) params.set('plan', plan);
  if (cycle) params.set('cycle', cycle);
  return <Navigate to={`/pricing?${params.toString()}`} replace />;
};

const LoadingFallback = () => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '100vh',
      bgcolor: '#0C0C0F',
    }}
  >
    <CircularProgress sx={{ color: '#4CC66E' }} />
  </Box>
);

function App() {
  return (
    <ThemeProvider theme={landingTheme}>
      <CssBaseline />
      <Router>
        <Toaster position="top-center" />
        <Suspense fallback={<LoadingFallback />}>
          <Routes>
            {/* Home & Marketing Landing Page */}
            <Route path="/" element={<LandingPage />} />
            <Route path="/signup-landingpage" element={<Navigate to="/" replace />} />

            {/* Pricing Page */}
            <Route path="/pricing" element={<PricingPage />} />

            {/* Auth Pages — Sign In / Sign Up */}
            <Route path="/auth" element={<AuthPage />} />
            <Route path="/signin" element={<Navigate to="/auth?tab=signin" replace />} />
            <Route path="/signup" element={<Navigate to="/auth?tab=signup" replace />} />

            {/* Payment & Onboarding Pages */}
            <Route path="/payment-success" element={<PaymentSuccessPage />} />
            <Route path="/payment/success" element={<PaymentSuccessPage />} />
            <Route path="/payment/failure" element={<PaymentFailureRedirect />} />
            <Route path="/thank-you" element={<ThankYouPage />} />
            <Route path="/stripe-configuration-required" element={<StripeConfigRequiredPage />} />
            <Route path="/stripe-config-required" element={<Navigate to="/stripe-configuration-required" replace />} />

            {/* Legal Pages */}
            <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
            <Route path="/privacy" element={<Navigate to="/privacy-policy" replace />} />
            <Route path="/terms-and-conditions" element={<TermsAndConditionsPage />} />
            <Route path="/terms" element={<Navigate to="/terms-and-conditions" replace />} />

            {/* Legacy route redirects */}
            <Route path="/plans" element={<Navigate to="/pricing" replace />} />
            <Route path="/app-plans" element={<Navigate to="/pricing" replace />} />
            <Route path="/app-signup" element={<Navigate to="/pricing" replace />} />
            <Route path="/checkout" element={<Navigate to="/pricing" replace />} />
            <Route path="/payment-cancel" element={<Navigate to="/pricing" replace />} />
            <Route path="/app-payment-success" element={<Navigate to="/payment-success" replace />} />
            <Route path="/app-payment-cancel" element={<Navigate to="/pricing" replace />} />

            {/* Catch-all */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
      </Router>
    </ThemeProvider>
  );
}

export default App;
